import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { aiProviders, prompts as promptsApi } from "../api/endpoints";
import ModelBadge from "./ModelBadge.jsx";

// Order we'd like the providers to render in when the server includes
// them — keeps ChatGPT / Claude / Gemini first, then the "Soon" tier.
const PROVIDER_ORDER = ["openai", "anthropic", "gemini", "perplexity", "google_ai_overview"];
const TOKEN_COST_PER_MODEL = 1;

/**
 * Modal that lets the user pick which LLMs to scan against and how much
 * the run will cost in tokens before dispatching the API call.
 *
 * Provider list is fetched from `/api/ai/providers/` so unconfigured
 * providers (no API key set) still appear in the picker with a "Soon"
 * badge — the user sees we plan to ship them, but cannot select them.
 */
export default function RunPromptModal({ websiteId, prompt, onClose, onDispatched }) {
  const { t } = useTranslation();
  const [providers, setProviders] = useState([]); // [{value, label, configured}]
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    aiProviders
      .list()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        list.sort(
          (a, b) =>
            PROVIDER_ORDER.indexOf(a.value) - PROVIDER_ORDER.indexOf(b.value),
        );
        setProviders(list);
        // Default-select every configured provider; unconfigured stay off.
        const initial = new Set(
          list.filter((p) => p.configured).map((p) => p.value),
        );
        setSelected(initial);
      })
      .catch(() => {
        // Fallback: assume the three classic providers are configured —
        // matches pre-Phase-6 behaviour so the modal still works offline.
        const fallback = [
          { value: "openai", label: "ChatGPT", configured: true },
          { value: "anthropic", label: "Claude", configured: true },
          { value: "gemini", label: "Gemini", configured: true },
        ];
        setProviders(fallback);
        setSelected(new Set(fallback.map((p) => p.value)));
      });
  }, []);

  function toggle(m, configured) {
    if (!configured) return; // "Soon" providers aren't selectable
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  }

  async function dispatch() {
    if (selected.size === 0) {
      setError(t("prompts.pickAtLeastOne"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const models = Array.from(selected);
      const r = await promptsApi.run(websiteId, prompt.id, models);
      onDispatched?.(r.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || t("prompts.couldNotStart"));
    } finally {
      setBusy(false);
    }
  }

  const cost = selected.size * TOKEN_COST_PER_MODEL;

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-20 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {t("prompts.runHeading")}
          </h3>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">
            {prompt.text}
          </p>
        </div>

        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">
            {t("prompts.selectModels")}
          </div>
          <div className="space-y-2">
            {providers.map((p) => {
              const configured = Boolean(p.configured);
              const on = selected.has(p.value);
              return (
                <label
                  key={p.value}
                  className={[
                    "flex items-center justify-between gap-3 rounded-lg border px-3 py-2 transition",
                    !configured
                      ? "border-slate-200 bg-slate-50/60 cursor-not-allowed opacity-70"
                      : on
                        ? "border-brand-500 bg-brand-50 cursor-pointer"
                        : "border-slate-200 hover:bg-slate-50 cursor-pointer",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={!configured}
                      onChange={() => toggle(p.value, configured)}
                      className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    />
                    <ModelBadge modelKey={p.value} />
                    {!configured && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 ring-1 ring-amber-200 rounded-full px-2 py-0.5">
                        {t("models.soon")}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 mono">
                    {TOKEN_COST_PER_MODEL} {t("billing.tokens")}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <span className="text-slate-600">{t("prompts.totalCost")}</span>
          <span className="mono font-semibold text-slate-900">
            {cost} {t("billing.tokens")}
          </span>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost" disabled={busy}>
            {t("common.cancel")}
          </button>
          <button
            onClick={dispatch}
            className="btn-primary"
            disabled={busy || selected.size === 0}
          >
            {busy ? t("prompts.running") : t("prompts.runSelected")}
          </button>
        </div>
      </div>
    </div>
  );
}
